import React, { useContext, useEffect, useState } from 'react';
import { ThemeContext } from 'styled-components';
// template components
import BulletedList from '@nmx/template/dist/componentLibrary/components/BulletedList';
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Link from '@nmx/template/dist/componentLibrary/components/Link';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
import SectionContainer from '@nmx/template/dist/componentLibrary/components/SectionContainer';
import Typography from '@nmx/template/dist/componentLibrary/foundations/Typography';
import WidowBlock from '@nmx/template/dist/componentLibrary/components/WidowBlock';
// styles
import {
  ColStyled,
  TableStyled,
} from '../tableStyles/styles';

export default () => {

  const theme = useContext(ThemeContext);
  const mediumUp = theme.mediumUp();
  const [isMediumUp, setIsMediumUp] = useState(mediumUp);
  const defaultTableHeadingWidth = '100px';
  const [tableHeadingWidth, setTableHeadingWidth] = useState(defaultTableHeadingWidth);

  const handleResize = (bool) => {
    setIsMediumUp(bool);
  };

  useEffect(() => {
    handleResize(mediumUp);
  }, [mediumUp]);

  useEffect(() => {
    setTableHeadingWidth(isMediumUp ? '275px' : defaultTableHeadingWidth);
  }, [isMediumUp]);

  const tableHeadingCol1 = 'Tax forms';
  const tableHeadingCol2 = <>What&#39;s reported to the IRS</>;

  const getTableHeading = (headingText) => {
    return (
      <Typography
        weight={400}
        themeType='darkTheme'
        disableBottomPadding>
        {headingText}
      </Typography>
    );
  };

  const getRowHeading = (rowHeadingText) => {
    return (
      <Typography
        weight={400}
        disableBottomPadding>
        {rowHeadingText}
      </Typography>
    );
  };

  return (
    <SectionContainer
      id='tax-documents-being-sent-out'
      backgroundVariant='lightC'
      themeType='lightTheme'>
      <Row align='center'>
        <Col>
          <Typography
            component='h2'
            align='center'
            nextElement='typography'>
            Which tax documents will be sent to you and <WidowBlock>the IRS&#63;</WidowBlock>
          </Typography>
          <Typography
            component='h3'
            align='center'
            nextElement='typography'>
            For retirement investment accounts
          </Typography>
        </Col>
      </Row>
      {/* start table 1 */}
      <Row align='center'>
        <ColStyled>
          <TableStyled
            variant='branded'
            tableData={{
              summary: 'This table shows which retirement investment account tax forms will be mailed to you and the IRS',
              tableHead: {
                rows: [
                  {
                    cols: [
                      {
                        children: getTableHeading(tableHeadingCol1),
                        width: tableHeadingWidth,
                      },
                      {
                        children: getTableHeading(tableHeadingCol2),
                        colScope: 'colgroup',
                      },
                    ],
                  },
                ],
              },
              tableBody: {
                rows: [
                  {
                    cols: [
                      {
                        children: getRowHeading(<>1099&#45;R</>),
                        isHeader: true,
                        rowScope: 'row',
                      },
                      {
                        children: (
                          <>
                            <BulletedList
                              bulletType='filledCircle'
                              bulletedListData={[
                                {
                                  bulletText: (
                                    <Typography
                                      weight={400}
                                      disableBottomPadding>
                                      Distributions including rollovers taken from your qualified retirement account. See{' '}
                                      <Link
                                        id='link-irs-publication-590b'
                                        variant='primaryDeemphasize'
                                        linkUrl='https://www.irs.gov/pub/irs-pdf/p590b.pdf'
                                        ariaLabel={<>Download the IRS Publication 590&#45;B PDF</>}
                                        isOutgoingLink
                                        noWrap>
                                        IRS Publication 590&#45;B
                                      </Link>.
                                    </Typography>
                                  ),
                                },
                                {
                                  bulletText: (
                                    <Typography
                                      weight={400}
                                      disableBottomPadding>
                                      Qualified retirement accounts transferred directly to outside financial firms &#40;a Trustee&#45;to&#45;Trustee transfer from one IRA to another&#41; will <em>not</em> generate a 1099&#45;R
                                    </Typography>
                                  ),
                                },
                              ]}
                            />
                          </>
                        ),
                      },
                    ],
                  },
                  {
                    cols: [
                      {
                        children: getRowHeading('5498'),
                        isHeader: true,
                        rowScope: 'row',
                      },
                      {
                        children: (
                          <>
                            <BulletedList
                              bulletType='filledCircle'
                              bulletedListData={[
                                {
                                  bulletText: (
                                    <Typography
                                      weight={400}
                                      disableBottomPadding>
                                      Contributions, including rollovers made to your retirement account. See{' '}
                                      <Link
                                        id='link-irs-publication-590a'
                                        variant='primaryDeemphasize'
                                        linkUrl='https://www.irs.gov/pub/irs-pdf/p590a.pdf'
                                        ariaLabel={<>Download the IRS Publication 590&#45;A PDF</>}
                                        isOutgoingLink
                                        noWrap>
                                        IRS Publication 590&#45;A
                                      </Link>
                                    </Typography>
                                  ),
                                },
                                {
                                  bulletText: (
                                    <Typography
                                      weight={400}
                                      disableBottomPadding>
                                      5498s are issued and mailed by May 31 for any prior year contributions and&#47;or rollovers. If May 31 falls on a weekend, issuance and mailing will occur on the previous business day.
                                    </Typography>
                                  ),
                                },
                                {
                                  bulletText: (
                                    <Typography
                                      weight={400}
                                      disableBottomPadding>
                                      See your statements for contributions made prior to receiving form 5498
                                    </Typography>
                                  ),
                                },
                              ]}
                            />
                          </>
                        ),
                      },
                    ],
                  },
                  {
                    cols: [
                      {
                        children: getRowHeading(<>990&#45;T</>),
                        isHeader: true,
                        rowScope: 'row',
                      },
                      {
                        children: (
                          <>
                            <BulletedList
                              bulletType='filledCircle'
                              bulletedListData={[
                                {
                                  bulletText: (
                                    <Typography
                                      weight={400}
                                      disableBottomPadding>
                                      Unrelated Business Taxable Income, known as UBTI for income generated over &#36;1,000 in a pretax qualified account &#40;such as an IRA&#41; has invested in a limited partnership &#40;LP&#41; and&#47;or master limited partnership &#40;MLP&#41;
                                    </Typography>
                                  ),
                                },
                              ]}
                            />
                          </>
                        ),
                      },
                    ],
                  },
                ],
              },
            }}
          />
        </ColStyled>
      </Row>
      {/* start table 2 */}
      <Row align='center'>
        <Col>
          <Typography
            component='h3'
            align='center'
            nextElement='typography'>
            For non&#45;retirement investment accounts
          </Typography>
        </Col>
        <ColStyled>
          <TableStyled
            variant='branded'
            tableData={{
              summary: <>This table shows which non&#45;retirement investment account tax forms will be mailed to you and the IRS</>,
              tableHead: {
                rows: [
                  {
                    cols: [
                      {
                        children: getTableHeading(tableHeadingCol1),
                        width: tableHeadingWidth,
                      },
                      {
                        children: getTableHeading(tableHeadingCol2),
                        colScope: 'colgroup',
                      },
                    ],
                  },
                ],
              },
              tableBody: {
                rows: [
                  {
                    cols: [
                      {
                        children: getRowHeading(<>1099&#45;B</>),
                        isHeader: true,
                        rowScope: 'row',
                      },
                      {
                        children: (
                          <Typography
                            weight={400}
                            disableBottomPadding>
                            Proceeds from broker and barter exchange transactions
                          </Typography>
                        ),
                      },
                    ],
                  },
                  {
                    cols: [
                      {
                        children: getRowHeading(<>1099&#45;INT</>),
                        isHeader: true,
                        rowScope: 'row',
                      },
                      {
                        children: (
                          <Typography
                            weight={400}
                            disableBottomPadding>
                            Interest income
                          </Typography>
                        ),
                      },
                    ],
                  },
                  {
                    cols: [
                      {
                        children: getRowHeading(<>1099&#45;DIV</>),
                        isHeader: true,
                        rowScope: 'row',
                      },
                      {
                        children: (
                          <Typography
                            weight={400}
                            disableBottomPadding>
                            Dividends and distributions
                          </Typography>
                        ),
                      },
                    ],
                  },
                  {
                    cols: [
                      {
                        children: getRowHeading(<>1099&#45;OID</>),
                        isHeader: true,
                        rowScope: 'row',
                      },
                      {
                        children: (
                          <Typography
                            weight={400}
                            disableBottomPadding>
                            Original issue discounts
                          </Typography>
                        ),
                      },
                    ],
                  },
                  {
                    cols: [
                      {
                        children: getRowHeading(<>1099&#45;MISC</>),
                        isHeader: true,
                        rowScope: 'row',
                      },
                      {
                        children: (
                          <Typography
                            weight={400}
                            disableBottomPadding>
                            Miscellaneous income
                          </Typography>
                        ),
                      },
                    ],
                  },
                ],
              },
            }}
          />
        </ColStyled>
      </Row>
      {/* start table 3 */}
      <Row align='center'>
        <Col>
          <Typography
            component='h3'
            align='center'
            nextElement='typography'>
            For life insurance &#38; annuities
          </Typography>
        </Col>
        <ColStyled>
          <TableStyled
            variant='branded'
            tableData={{
              summary: <>This table shows which life insurance &#38; annuities tax forms will be mailed to you and the IRS</>,
              tableHead: {
                rows: [
                  {
                    cols: [
                      {
                        children: getTableHeading(tableHeadingCol1),
                        width: tableHeadingWidth,
                      },
                      {
                        children: getTableHeading(tableHeadingCol2),
                        colScope: 'colgroup',
                      },
                    ],
                  },
                ],
              },
              tableBody: {
                rows: [
                  {
                    cols: [
                      {
                        children: getRowHeading(
                          <>
                            <Typography
                              component='span'
                              disableBottomPadding
                              block>
                              1099
                            </Typography>
                            <Typography
                              component='span'
                              disableBottomPadding
                              block>
                              1099&#45;R
                            </Typography>
                          </>,
                        ),
                        isHeader: true,
                        rowScope: 'row',
                      },
                      {
                        children: (
                          <BulletedList
                            bulletType='filledCircle'
                            bulletedListData={[
                              {
                                bulletText: (
                                  <Typography
                                    weight={400}
                                    disableBottomPadding>
                                    Pension and annuity payments
                                  </Typography>
                                ),
                              },
                              {
                                bulletText: (
                                  <Typography
                                    weight={400}
                                    disableBottomPadding>
                                    Distributions of &#36;10 or more from your retirement plan
                                  </Typography>
                                ),
                              },
                              {
                                bulletText: (
                                  <Typography
                                    weight={400}
                                    disableBottomPadding>
                                    For life insurance: A full surrender, partial withdrawal, loan or dividend transaction
                                  </Typography>
                                ),
                              },
                              {
                                bulletText: (
                                  <Typography
                                    weight={400}
                                    disableBottomPadding>
                                    For an annuity: A full surrender, a partial withdrawal, the transfer of the contract to a new owner, or dividend transaction
                                  </Typography>
                                ),
                              },
                            ]}
                          />
                        ),
                      },
                    ],
                  },
                  {
                    cols: [
                      {
                        children: getRowHeading(<>1099&#45;INT</>),
                        isHeader: true,
                        rowScope: 'row',
                      },
                      {
                        children: (
                          <>
                            <BulletedList
                              bulletType='filledCircle'
                              bulletedListData={[
                                {
                                  bulletText:
                                (
                                  <Typography
                                    weight={400}
                                    disableBottomPadding>
                                    Earned interest of &#36;10 or more from a bank, brokerage or other financial institution
                                  </Typography>
                                ),
                                },
                              ]}
                            />
                          </>
                        ),
                      },
                    ],
                  },
                  {
                    cols: [
                      {
                        children: getRowHeading(<>1099&#45;NEC</>),
                        isHeader: true,
                        rowScope: 'row',
                      },
                      {
                        children: (
                          <>
                            <BulletedList
                              bulletType='filledCircle'
                              bulletedListData={[
                                {
                                  bulletText: (
                                    <Typography
                                      weight={400}
                                      disableBottomPadding>
                                      Non&#45;employee compensation (self&#45;employment income)
                                    </Typography>
                                  ),
                                },
                              ]}
                            />
                          </>
                        ),
                      },
                    ],
                  },
                  {
                    cols: [
                      {
                        children: getRowHeading(<>1099&#45;MISC</>),
                        isHeader: true,
                        rowScope: 'row',
                      },
                      {
                        children: (
                          <>
                            <BulletedList
                              bulletType='filledCircle'
                              bulletedListData={[
                                {
                                  bulletText: (
                                    <Typography
                                      weight={400}
                                      disableBottomPadding>
                                      Miscellaneous income
                                    </Typography>
                                  ),
                                },
                              ]}
                            />
                          </>
                        ),
                      },
                    ],
                  },
                ],
              },
            }}
          />
        </ColStyled>
      </Row>
    </SectionContainer>
  );
};
